import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BatteryCharging, BedDouble, Camera, UtensilsCrossed, MapPin, Clock, Route, Zap, RefreshCw, ThermometerSun, Palmtree, Check, ExternalLink, AlertTriangle, Flag, Home, Trash2, Undo2, ChevronDown, Minus, Plus, Scissors, Pencil, Fuel, Trophy, Lock, X, Calculator } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { confirmDialog } from "./ConfirmHost";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { StageShareButton } from "./StageShareButton";
import { RouteStopsDialog } from "./RouteStopsDialog";
import { useGeocodePreview, GeocodePreview } from "./GeocodePreview";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { infoUrl } from "../lib/links";
import { powerColor } from "../lib/power";
import { findNetwork } from "../lib/chargerNetworks";
import { isTripToday, buildRouteInfo, kmOnRoute, dayWindow } from "../lib/geo";
import { SocChart } from "./SocChart";
import { VignetteDayNote } from "./VignetteAdvice";
import { ChargeCalcDialog } from "./ChargeCalculator";

const fmtDate = (d) => (d || "").split("-").reverse().join("-");

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const WMO_NL = { 0: "zonnig", 1: "licht bewolkt", 2: "bewolkt", 3: "zwaar bewolkt", 45: "mist", 48: "mist", 51: "motregen", 53: "motregen", 55: "motregen", 61: "regen", 63: "regen", 65: "zware regen", 71: "sneeuw", 73: "sneeuw", 75: "zware sneeuw", 80: "buien", 81: "buien", 82: "zware buien", 95: "onweer", 96: "onweer", 99: "onweer" };

const LiveWeatherToday = ({ day }) => {
  const [w, setW] = useState(null);
  const pt = day.end || day.start || {};
  useEffect(() => {
    if (pt.lat == null) return;
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${pt.lat}&longitude=${pt.lon}&current=temperature_2m,weather_code&daily=temperature_2m_max&forecast_days=1&timezone=auto`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.current?.temperature_2m != null) {
          setW({ temp: Math.round(d.current.temperature_2m), code: d.current.weather_code, max: Math.round(d.daily?.temperature_2m_max?.[0] ?? d.current.temperature_2m) });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pt.lat, pt.lon]);
  if (!w) {
    return day.weather ? (
      <span className="flex items-center gap-1.5" data-testid={`weather-day-${day.day_number}`}><ThermometerSun className="w-4 h-4 text-amber-500" /><span className="font-mono">{day.weather.temp_max}°C</span><span className="text-xs">{day.weather.description}</span></span>
    ) : null;
  }
  return (
    <span className="flex items-center gap-1.5" data-testid={`live-weather-day-${day.day_number}`}>
      <ThermometerSun className="w-4 h-4 text-amber-500" />
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-red-500/10 text-red-600">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
      </span>
      <span className="font-mono">nu {w.temp}°C</span>
      <span className="text-xs">{WMO_NL[w.code] || ""}{w.max > w.temp ? ` · max ${w.max}°C` : ""}</span>
    </span>
  );
};

const addMinutes = (hhmm, mins) => {
  const [h, m] = (hhmm || "09:00").split(":").map(Number);
  const total = (((h * 60 + m + Math.round(mins)) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const DepartureTime = ({ trip, day, readOnly, onRefresh }) => {
  const extTime = (trip.departure_times || {})[String(day.day_number)] || trip.departure_time || "09:00";
  const [time, setTime] = useState(extTime);
  useEffect(() => { setTime(extTime); }, [extTime]);
  const chargeMin = (day.charging_stops || []).reduce((s, c) => s + (c.charge_minutes || 0), 0);
  const totalMins = (day.drive_time_hours || 0) * 60 + chargeMin;
  const arrival = addMinutes(time, totalMins);
  const save = async (val) => {
    setTime(val);
    if (!val || !/^[0-2]\d:[0-5]\d$/.test(val)) return;
    try {
      await api.patch(`/trips/${trip.id}/departure`, { day_number: day.day_number, time: val });
      toast.success(`Vertrektijd dag ${day.day_number}: ${val} — aankomst ±${addMinutes(val, totalMins)}`, { duration: 2500 });
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const saveArrival = (val) => {
    if (!val || !/^[0-2]\d:[0-5]\d$/.test(val)) return;
    save(addMinutes(val, -totalMins));
  };
  return (
    <span className="flex items-center gap-1.5" data-testid={`departure-row-${day.day_number}`}>
      <Clock className="w-4 h-4 text-primary" />
      {readOnly ? (
        <span className="font-mono">{time}</span>
      ) : (
        <input data-testid={`departure-time-input-${day.day_number}`} type="time" value={time} onChange={(e) => save(e.target.value)}
          title="Vertrekuur voor deze dag aanpassen"
          className="font-mono text-sm bg-transparent border-b border-dashed border-border focus:border-primary outline-none w-[76px] cursor-pointer" />
      )}
      {readOnly ? (
        <span className="font-mono" data-testid={`arrival-time-${day.day_number}`}>→ aankomst ±{arrival}</span>
      ) : (
        <span className="font-mono flex items-center gap-1" data-testid={`arrival-time-${day.day_number}`}>
          → aankomst ±
          <input data-testid={`arrival-time-input-${day.day_number}`} type="time" value={arrival} onChange={(e) => saveArrival(e.target.value)}
            title="Gewenste aankomsttijd — we rekenen uit hoe laat je moet vertrekken"
            className="font-mono text-sm bg-transparent border-b border-dashed border-border focus:border-primary outline-none w-[76px] cursor-pointer" />
        </span>
      )}
    </span>
  );
};

const DayBattery = ({ trip, day, defaultSoc, readOnly, onRefresh }) => {
  const value = day.start_soc_override ?? defaultSoc;
  const overridden = day.start_soc_override != null;
  const [pct, setPct] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPct(value); }, [value]);
  const apply = async (soc) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/trips/${trip.id}/days/${day.day_number}/battery`, { start_soc: soc });
      toast.success(soc == null
        ? "Terug naar standaard — laadstops herrekend"
        : `Dag ${day.day_number} start met ${soc}% — ${data.charging_stops.length} laadstop${data.charging_stops.length === 1 ? "" : "s"}, aankomst ~${data.arrival_soc}%`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (readOnly) {
    return (
      <span className="flex items-center gap-1.5">
        <BatteryCharging className="w-4 h-4 text-primary" />
        <span className="font-mono">start {value}%</span>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}
      title="Batterij bij vertrek van deze dag — de laadstops worden herrekend. Hotel zonder lader? Zet hier je échte % van 's ochtends.">
      <BatteryCharging className={`w-4 h-4 ${overridden ? "text-[#3A6EA5]" : "text-primary"}`} />
      <span className={`font-mono ${overridden ? "text-[#3A6EA5] font-bold" : ""}`}>start</span>
      <input data-testid={`day-soc-slider-${day.day_number}`} type="range" min="5" max="100" step="5" value={pct} disabled={busy}
        onChange={(e) => setPct(parseInt(e.target.value))} className="w-20 sm:w-24 accent-[#2D6A4F]" />
      <span data-testid={`day-soc-value-${day.day_number}`} className={`font-mono ${overridden ? "text-[#3A6EA5] font-bold" : ""}`}>
        {pct}%{overridden && pct === value ? " ✎" : ""}
      </span>
      {pct !== value && (
        <button data-testid={`day-soc-apply-${day.day_number}`} type="button" disabled={busy} onClick={() => apply(pct)}
          className="text-[11px] font-bold text-white bg-primary hover:bg-[#1B4332] rounded-full px-2.5 py-0.5 active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Herrekenen..." : "Herbereken"}
        </button>
      )}
      {overridden && pct === value && (
        <button data-testid={`day-soc-reset-${day.day_number}`} type="button" disabled={busy} onClick={() => apply(null)}
          title="Terug naar standaard"
          className="text-[11px] font-semibold text-muted-foreground hover:text-primary underline decoration-dashed transition-[color] duration-200">
          standaard
        </button>
      )}
    </span>
  );
};

const EditEndpointDialog = ({ trip, kind, current, day, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const isOrigin = kind === "origin";
  const isStop = kind === "stopover";
  const isReturnEnd = kind === "return_end";
  const label = isOrigin ? "Vertrekpunt" : isStop ? "Tussenstop" : isReturnEnd ? "Eindpunt terugreis" : "Eindbestemming";
  const suffix = day ? `-${day.day_number}` : "";
  const geo = useGeocodePreview(value);
  const confirmedName = geo.result ? (geo.result.name || "").split(",")[0] : null;
  const apply = async () => {
    const v = value.trim();
    if (v.length < 2 || !geo.result) return;
    setBusy(true);
    try {
      if (isStop) {
        await api.patch(`/trips/${trip.id}/days/${day.day_number}/overnight`, { location: v });
      } else {
        await api.patch(`/trips/${trip.id}/endpoints`, isOrigin ? { origin: v } : isReturnEnd ? { return_end: v } : { destination: v });
      }
      toast.success(`${label} gewijzigd naar ${confirmedName || v} — de route wordt opnieuw berekend!`);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button data-testid={`edit-${kind}-button${suffix}`} type="button" title={`${label} wijzigen — de route wordt herberekend`}
        onClick={(e) => { e.stopPropagation(); setValue(""); setOpen(true); }}
        className="p-1.5 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-[background-color,color] duration-200 inline-flex align-middle">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`edit-${kind}-dialog${suffix}`} className="rounded-2xl max-w-sm" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-heading">{label} wijzigen</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Huidig: <strong>{(current || "").split(",")[0]}</strong>. Vul de nieuwe plaats in — de volledige route wordt opnieuw berekend.
            {isStop && " De overnachting verhuist naar de nieuwe plaats; de rest van je planning blijft behouden."}
            {isReturnEnd && " De terugreis eindigt dan op deze plek in plaats van op je vertrekpunt."}
            {kind === "destination" && " Je tussenstops blijven behouden en de overnachting op de eindbestemming verhuist mee naar de nieuwe plaats."}
            {isOrigin && " Je tussenstops en overnachtingen blijven behouden."}
            {isOrigin && trip.round_trip && " Dit is een heen-en-terug trip: je thuiskomst-punt wijzigt dan ook mee."}
          </p>
          <Input data-testid={`edit-${kind}-input${suffix}`} autoFocus value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={isStop ? 'bv. "Reims, Frankrijk"' : 'bv. "Sint-Amands, België"'} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} className="rounded-xl" />
          <GeocodePreview value={value} geo={geo} testId={`edit-${kind}-geo${suffix}`} />
          <Button data-testid={`edit-${kind}-apply${suffix}`} onClick={apply} disabled={busy || value.trim().length < 2 || geo.checking || !geo.result}
            className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
            {busy ? "Wijzigen..." : confirmedName ? `Wijzig naar ${confirmedName} & herbereken` : "Wijzig & herbereken route"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ExtraNightsDialog = ({ trip, day, onRefresh, testId }) => {
  const [open, setOpen] = useState(false);
  const [nights, setNights] = useState(1);
  const [busy, setBusy] = useState(false);
  const place = ((day.end?.name || "").split(",")[0]) || "deze plek";
  const apply = async () => {
    setBusy(true);
    try {
      await api.post(`/trips/${trip.id}/days/${day.day_number}/extra-night`, { nights });
      toast.success(`${nights} extra nacht${nights > 1 ? "en" : ""} in ${place} bijgeboekt — je planning wordt opnieuw berekend`);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setNights(1); }}>
      <DialogTrigger asChild>
        <button data-testid={testId || `extra-night-${day.day_number}`}
          className="text-xs font-semibold text-[#3A6EA5] hover:text-[#2d5884] flex items-center gap-1.5 transition-[color] duration-200"
          title="Blijf hier één of meerdere nachten langer — je trip wordt langer en de planning wordt herberekend">
          <BedDouble className="w-3.5 h-3.5" /> Blijf hier langer
        </button>
      </DialogTrigger>
      <DialogContent data-testid={`extra-nights-dialog-${day.day_number}`} className="rounded-2xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">Langer blijven in {place}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Kies hoeveel extra nachten je hier blijft. Je trip wordt <strong>{nights} dag{nights > 1 ? "en" : ""} langer</strong> en de volledige planning wordt opnieuw berekend.
        </p>
        <div className="flex items-center justify-center gap-5 py-2">
          <button data-testid={`extra-nights-minus-${day.day_number}`} type="button" onClick={() => setNights((n) => Math.max(1, n - 1))}
            className="w-10 h-10 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
            <Minus className="w-4 h-4" />
          </button>
          <span data-testid={`extra-nights-value-${day.day_number}`} className="font-mono font-bold text-2xl w-10 text-center">{nights}</span>
          <button data-testid={`extra-nights-plus-${day.day_number}`} type="button" onClick={() => setNights((n) => Math.min(14, n + 1))}
            className="w-10 h-10 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <Button data-testid={`extra-nights-apply-${day.day_number}`} onClick={apply} disabled={busy}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Bijboeken..." : `Boek ${nights} extra nacht${nights > 1 ? "en" : ""} & herbereken`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const SplitDayDialog = ({ trip, day, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const total = Math.round(day.distance_km || 0);
  const [km, setKm] = useState(Math.min(Math.max(20, Math.round(total / 2 / 5) * 5), Math.max(total - 20, 21)));
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${day.day_number}/split`, { km });
      toast.success(`Overnachting rond ${data.location} toegevoegd — je planning wordt opnieuw berekend`);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`split-day-${day.day_number}`}
          className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-[color] duration-200"
          title="Splits deze rijdag in twee dagen met een overnachting onderweg">
          <Scissors className="w-3.5 h-3.5" /> Splits deze rijdag met een overnachting
        </button>
      </DialogTrigger>
      <DialogContent data-testid={`split-day-dialog-${day.day_number}`} className="rounded-2xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">Rijdag {day.day_number} splitsen</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          Kies waar je ongeveer wilt overnachten. We zoeken de dichtstbijzijnde plaats op de route, je trip wordt <strong>1 dag langer</strong> en de hele planning (route, laadstops, verblijf) wordt opnieuw berekend.
        </p>
        <div>
          <div className="flex items-center justify-between text-xs font-semibold">
            <span>Overnachting na</span>
            <span className="font-mono font-bold text-primary">{km} km</span>
          </div>
          <input data-testid={`split-day-slider-${day.day_number}`} type="range" min="20" max={Math.max(total - 20, 21)} step="5" value={km}
            onChange={(e) => setKm(parseInt(e.target.value))} className="w-full accent-[#2D6A4F] mt-1" />
          <p className="text-[11px] text-muted-foreground font-mono">dag 1: {km} km · dag 2: {Math.max(total - km, 0)} km</p>
        </div>
        <Button data-testid={`split-day-apply-${day.day_number}`} onClick={apply} disabled={busy}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Plaats zoeken..." : "Splits & herbereken trip"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const AddStopoverDialog = ({ trip, onRefresh, dayNumber }) => {
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState("");
  const [nights, setNights] = useState(1);
  const [tab, setTab] = useState("ai");
  const [stayName, setStayName] = useState("");
  const [stayUrl, setStayUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const geo = useGeocodePreview(location);
  const confirmedName = geo.result ? (geo.result.name || "").split(",")[0] : null;
  const apply = async () => {
    if (!location.trim() || !geo.result) { toast.error("Vul een plaats in en wacht op de plaatsbevestiging"); return; }
    if (tab === "own" && !stayName.trim()) { toast.error("Geef je verblijf een naam"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/stopovers`, {
        location: location.trim(), nights,
        stay_name: tab === "own" ? stayName.trim() : null,
        stay_url: tab === "own" ? (stayUrl.trim() || null) : null,
      });
      toast.success(`Tussenstop ${data.location} (${nights} nacht${nights > 1 ? "en" : ""}) toegevoegd — je planning wordt opnieuw berekend!`);
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setLocation(""); setNights(1); setTab("ai"); setStayName(""); setStayUrl(""); } }}>
      <DialogTrigger asChild>
        <button data-testid={`add-stopover-button-${dayNumber}`}
          className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-[color] duration-200"
          title="Voeg een extra tussenstop met overnachting toe — de route wordt opnieuw berekend">
          <Plus className="w-3.5 h-3.5" /> Extra tussenstop
        </button>
      </DialogTrigger>
      <DialogContent data-testid="add-stopover-dialog" className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Extra tussenstop inplannen</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          De tussenstop wordt op de juiste plek in je route gezet, je trip wordt <strong>{nights} nacht{nights > 1 ? "en" : ""} langer</strong> en de hele planning (route, laadstops, verblijf) wordt opnieuw berekend.
        </p>
        <Input data-testid="stopover-location-input" value={location} onChange={(e) => setLocation(e.target.value)}
          placeholder='Plaats (bijv. "Dijon, Frankrijk")' className="rounded-xl" autoFocus />
        <GeocodePreview value={location} geo={geo} testId="stopover-geo" />
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold">Aantal nachten</span>
          <div className="flex items-center gap-2">
            <button data-testid="stopover-nights-minus" type="button" onClick={() => setNights((n) => Math.max(1, n - 1))}
              className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span data-testid="stopover-nights-value" className="font-mono font-bold text-lg w-8 text-center">{nights}</span>
            <button data-testid="stopover-nights-plus" type="button" onClick={() => setNights((n) => Math.min(14, n + 1))}
              className="w-8 h-8 rounded-full bg-secondary hover:bg-primary/15 flex items-center justify-center active:scale-90 transition-[transform,background-color] duration-150">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold mb-1.5">Verblijf</p>
          <div className="flex gap-1.5">
            <button type="button" data-testid="stopover-stay-tab-ai" onClick={() => setTab("ai")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${tab === "ai" ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-background text-muted-foreground border-border hover:border-[#3A6EA5]/50"}`}>
              AI stelt een verblijf voor
            </button>
            <button type="button" data-testid="stopover-stay-tab-own" onClick={() => setTab("own")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${tab === "own" ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-background text-muted-foreground border-border hover:border-[#3A6EA5]/50"}`}>
              Eigen boeking
            </button>
          </div>
          {tab === "own" && (
            <div className="space-y-2 mt-2.5">
              <Input data-testid="stopover-stay-name-input" value={stayName} onChange={(e) => setStayName(e.target.value)}
                placeholder="Naam van je verblijf (bijv. Hotel Bellevue)" className="rounded-xl" />
              <Input data-testid="stopover-stay-url-input" value={stayUrl} onChange={(e) => setStayUrl(e.target.value)}
                placeholder="Link van Booking.com of Airbnb (optioneel)" className="rounded-xl" />
            </div>
          )}
        </div>
        <Button data-testid="stopover-apply-button" onClick={apply} disabled={busy || !location.trim() || geo.checking || !geo.result || (tab === "own" && !stayName.trim())}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Toevoegen..." : confirmedName ? `Voeg ${confirmedName} toe & herbereken` : "Voeg toe & herbereken trip"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const ViaPointsDialog = ({ trip, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const geo = useGeocodePreview(input);
  const resolvedLabel = () => {
    if (!geo.result) return null;
    const name = (geo.result.name || "").split(",")[0];
    const country = (geo.result.full_name || "").split(",").slice(-1)[0]?.trim();
    return country && !name.toLowerCase().includes(country.toLowerCase()) ? `${name}, ${country}` : name;
  };
  const add = () => {
    const label = resolvedLabel();
    if (!label) return;
    if (list.some((w) => w.toLowerCase() === label.toLowerCase())) { toast.error("Dit via-punt staat al in de lijst"); return; }
    setList((l) => [...l, label]);
    setInput("");
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/trips/${trip.id}/waypoints`, { waypoints: list });
      toast.success("Via-punten opgeslagen — je route wordt opnieuw berekend!");
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const count = (trip.waypoints || []).length;
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setList([...(trip.waypoints || [])]); setInput(""); } }}>
      <DialogTrigger asChild>
        <button data-testid="via-points-button"
          className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-[color] duration-200"
          title="Stuur de route via bepaalde plaatsen, zonder er te overnachten — zoals op Google Maps">
          <Route className="w-3.5 h-3.5" /> Rij via…{count > 0 ? ` (${count})` : ""}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="via-points-dialog" className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Rij via…</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          De route wordt langs deze plaatsen gestuurd — <strong>zonder overnachting of extra dag</strong>. Wil je ergens slapen? Gebruik dan "Extra tussenstop".
        </p>
        {list.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {list.map((w, i) => (
              <span key={`${w}-${i}`} data-testid={`via-chip-${i}`}
                className="inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-xs font-semibold">
                {w}
                <button type="button" data-testid={`via-chip-remove-${i}`} onClick={() => setList((l) => l.filter((_, j) => j !== i))}
                  className="p-0.5 rounded-full hover:bg-red-500/15 hover:text-red-500 transition-[background-color,color] duration-150">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input data-testid="via-points-input" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder='Plaats (bijv. "Luxemburg")' className="rounded-xl flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <Button data-testid="via-points-add" type="button" variant="secondary" onClick={add} disabled={geo.checking || !geo.result}
            className="rounded-xl shrink-0">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <GeocodePreview value={input} geo={geo} testId="via-points-geo" />
        <Button data-testid="via-points-save" onClick={save} disabled={busy}
          className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
          {busy ? "Opslaan..." : "Opslaan & herbereken route"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const AddCharger = ({ trip, day, onRefresh }) => {
  const [alts, setAlts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("name");
  const [addrLoc, setAddrLoc] = useState(null);
  const [customName, setCustomName] = useState("");
  const [customKw, setCustomKw] = useState(250);
  const mid = {
    lat: ((day.start?.lat ?? 0) + (day.end?.lat ?? 0)) / 2,
    lon: ((day.start?.lon ?? 0) + (day.end?.lon ?? 0)) / 2,
  };
  const load = async (q = "") => {
    setLoading(true);
    setAddrLoc(null);
    try {
      if (mode === "address" && q) {
        const { data } = await api.get("/chargers/near-address", { params: { q } });
        setAddrLoc(data.location);
        setAlts(data.chargers.slice(0, 12));
      } else {
        const params = q ? { lat: mid.lat, lon: mid.lon, radius: 120, q } : { lat: mid.lat, lon: mid.lon, radius: 60, min_kw: 50 };
        const { data } = await api.get("/chargers/near", { params });
        setAlts(data.slice(0, 10));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  const choose = async (a) => {
    setBusy(true);
    try {
      await api.post(`/trips/${trip.id}/days/${day.day_number}/charging`, {
        ocm_id: a.ocm_id, name: a.name, town: a.town, lat: a.lat, lon: a.lon,
        operator: a.operator, max_power_kw: a.max_power_kw, stalls: a.stalls ?? null, connectors: a.connectors || [],
      });
      toast.success("Laadstop toegevoegd!");
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const addCustom = async () => {
    const name = customName.trim() || `Laadpunt ${addrLoc.name}`;
    setBusy(true);
    try {
      await api.post(`/trips/${trip.id}/days/${day.day_number}/charging`, {
        name, town: addrLoc.name, lat: addrLoc.lat, lon: addrLoc.lon,
        operator: /supercharger|tesla/i.test(name) ? "Tesla" : null,
        max_power_kw: Number(customKw), connectors: [],
      });
      toast.success("Laadstop toegevoegd op het adres! We geven dit laadpunt door zodat het voor alle reizigers vindbaar wordt.", { duration: 6000 });
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v && !alts) load(); }}>
      <DialogTrigger asChild>
        <button data-testid={`add-charger-button-${day.day_number}`} title="Zoek en voeg zelf een laadpunt toe aan deze dag"
          className="text-xs font-semibold text-primary hover:text-primary/80 flex items-center gap-1 transition-[color] duration-200">
          <Plus className="w-3.5 h-3.5" /> Toevoegen
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Laadstop toevoegen — dag {day.day_number}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          <button type="button" data-testid="add-charger-mode-name" onClick={() => setMode("name")}
            className={`rounded-full text-[11px] font-bold px-3 py-1.5 transition-[background-color,color] duration-200 ${mode === "name" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Op naam
          </button>
          <button type="button" data-testid="add-charger-mode-address" onClick={() => setMode("address")}
            className={`rounded-full text-[11px] font-bold px-3 py-1.5 transition-[background-color,color] duration-200 ${mode === "address" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Op adres
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(query.trim()); }} className="flex gap-2">
          <input data-testid="add-charger-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === "address" ? 'Adres of straat, bijv. "Hadikgasse 256, 1140 Wien"' : 'Zoek op naam, bijv. "Wienerberg"'}
            className="flex-1 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]" />
          <button type="submit" data-testid="add-charger-search-button" disabled={loading || (mode === "address" && query.trim().length < 3)}
            className="rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
            Zoek
          </button>
        </form>
        <p className="text-[11px] text-muted-foreground -mt-1">
          {mode === "address"
            ? "Vind alle laadpunten (incl. Tesla Superchargers) binnen 10 km van een adres, straat of plek — gesorteerd op afstand."
            : <>Standaard: snelladers (≥50 kW) rond het midden van {(day.start?.name || "").split(",")[0]} → {(day.end?.name || "").split(",")[0]}, incl. Tesla Superchargers. Zoeken kijkt tot 120 km ver.</>}
        </p>
        {addrLoc && !loading && (
          <p data-testid="add-charger-address-result" className="text-xs font-semibold text-primary -mt-1">
            Laadpunten bij: {addrLoc.name}
          </p>
        )}
        {loading && <p className="text-sm text-muted-foreground py-4">Laadpunten zoeken...</p>}
        {alts && !loading && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {alts.length === 0 && <p className="text-sm text-muted-foreground">{addrLoc ? `Geen laadpunten binnen 10 km van ${addrLoc.name} gevonden.` : "Geen laadpunten gevonden — probeer een andere zoekterm."}</p>}
            {alts.map((a) => (
              <div key={a.ocm_id} className="bg-secondary rounded-xl p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-1.5 min-w-0" title={a.name}>
                    <span className="truncate">{a.name}</span>
                    {(a.operator === "Tesla" || /supercharger/i.test(a.name)) && <span className="shrink-0 text-[9px] font-bold bg-red-500/10 text-red-600 rounded-full px-1.5 py-0.5">TESLA</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">{a.max_power_kw ? `${a.max_power_kw} kW` : "? kW"}{a.stalls != null ? ` · ${a.stalls} palen` : ""} · ±{a.distance_km} km {addrLoc ? "van het adres" : "van het midden van de rit"}{a.town ? ` · ${a.town}` : ""}</p>
                </div>
                <button data-testid={`add-charger-choose-${a.ocm_id}`} disabled={busy} onClick={() => choose(a)}
                  className="shrink-0 rounded-full bg-primary text-primary-foreground text-xs font-bold px-3.5 py-1.5 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
                  Voeg toe
                </button>
              </div>
            ))}
          </div>
        )}
        {addrLoc && !loading && alts && (
          <div data-testid="add-charger-custom-card" className="border border-dashed border-primary/40 rounded-xl p-3 space-y-2">
            <p className="text-xs font-semibold">Staat jouw laadpunt er niet bij?</p>
            <p className="text-[11px] text-muted-foreground">Voeg het dan zelf toe, precies op dit adres ({addrLoc.name}).</p>
            <div className="flex gap-2">
              <input data-testid="add-charger-custom-name" value={customName} onChange={(e) => setCustomName(e.target.value)}
                placeholder="Naam, bijv. Tesla Supercharger"
                className="flex-1 min-w-0 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]" />
              <select data-testid="add-charger-custom-kw" value={customKw} onChange={(e) => setCustomKw(e.target.value)}
                className="shrink-0 bg-secondary rounded-xl px-2 py-2 text-sm outline-none">
                {[50, 150, 250, 300, 400].map((kw) => <option key={kw} value={kw}>{kw} kW</option>)}
              </select>
            </div>
            <button data-testid="add-charger-custom-submit" disabled={busy} onClick={addCustom}
              className="w-full rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
              {busy ? "Toevoegen..." : "Voeg toe op dit adres"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AlternativeChargers = ({ stop, tripId, dayNumber, stopIndex, vehicle, onRefresh }) => {
  const [alts, setAlts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const chargeMinutes = (kw) => {
    if (!vehicle?.battery_kwh || !vehicle?.max_charge_kw) return null;
    const energy = ((stop.charge_to_soc || 80) / 100 - (stop.arrival_soc || 15) / 100) * vehicle.battery_kwh;
    const eff = Math.min(kw || 50, vehicle.max_charge_kw) * 0.6;
    return Math.round((energy / Math.max(eff, 10)) * 60);
  };
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("name");
  const [addrLoc, setAddrLoc] = useState(null);
  const [customName, setCustomName] = useState("");
  const [customKw, setCustomKw] = useState(250);
  const [busy, setBusy] = useState(false);

  const load = async (q = "") => {
    setLoading(true);
    setAddrLoc(null);
    try {
      if (mode === "address" && q) {
        const { data } = await api.get("/chargers/near-address", { params: { q } });
        setAddrLoc(data.location);
        setAlts(data.chargers.filter((a) => a.ocm_id !== stop.ocm_id).slice(0, 10));
      } else {
        const params = q
          ? { lat: stop.lat, lon: stop.lon, radius: 100, q }
          : { lat: stop.lat, lon: stop.lon, radius: 40, min_kw: 50 };
        let { data } = await api.get("/chargers/near", { params });
        let list = data.filter((a) => a.ocm_id !== stop.ocm_id);
        if (!q && list.length === 0) {
          ({ data } = await api.get("/chargers/near", { params: { lat: stop.lat, lon: stop.lon, radius: 40 } }));
          list = data.filter((a) => a.ocm_id !== stop.ocm_id);
        }
        setAlts(list.slice(0, 10));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const choose = async (a) => {
    try {
      await api.patch(`/trips/${tripId}/days/${dayNumber}/charging/${stopIndex}`, a);
      toast.success("Laadstop vervangen!");
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const chooseCustom = async () => {
    const name = customName.trim() || `Laadpunt ${addrLoc.name}`;
    setBusy(true);
    try {
      await api.patch(`/trips/${tripId}/days/${dayNumber}/charging/${stopIndex}`, {
        name, town: addrLoc.name, lat: addrLoc.lat, lon: addrLoc.lon,
        operator: /supercharger|tesla/i.test(name) ? "Tesla" : null,
        max_power_kw: Number(customKw), connectors: [],
      });
      toast.success("Laadstop vervangen! We geven dit laadpunt door zodat het voor alle reizigers vindbaar wordt.", { duration: 6000 });
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !alts) load(); }}>
      <DialogTrigger asChild>
        <button data-testid={`alternative-chargers-button-${dayNumber}-${stopIndex}`} className="text-xs font-semibold text-primary hover:text-[#1B4332] flex items-center gap-1 transition-[color] duration-200">
          <RefreshCw className="w-3 h-3" /> Vervang
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Alternatieve laadpunten</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          <button type="button" data-testid="alt-charger-mode-name" onClick={() => setMode("name")}
            className={`rounded-full text-[11px] font-bold px-3 py-1.5 transition-[background-color,color] duration-200 ${mode === "name" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Op naam
          </button>
          <button type="button" data-testid="alt-charger-mode-address" onClick={() => setMode("address")}
            className={`rounded-full text-[11px] font-bold px-3 py-1.5 transition-[background-color,color] duration-200 ${mode === "address" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            Op adres
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(query.trim()); }} className="flex gap-2">
          <input data-testid="charger-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === "address" ? 'Adres of straat, bijv. "Hadikgasse 256, 1140 Wien"' : 'Zoek op naam, bijv. "Wienerberg"'}
            className="flex-1 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]" />
          <button type="submit" data-testid="charger-search-button" disabled={loading || (mode === "address" && query.trim().length < 3)}
            className="rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
            Zoek
          </button>
        </form>
        <p className="text-[11px] text-muted-foreground -mt-1">
          {mode === "address"
            ? "Vind alle laadpunten (incl. Tesla Superchargers) binnen 10 km van een adres, straat of plek — gesorteerd op afstand."
            : "Standaard: snelladers (≥50 kW) tot 40 km verderop, incl. Tesla Superchargers. Zoeken kijkt tot 100 km ver."}
        </p>
        {addrLoc && !loading && (
          <p data-testid="alt-charger-address-result" className="text-xs font-semibold text-primary -mt-1">
            Laadpunten bij: {addrLoc.name}
          </p>
        )}
        {loading && <p className="text-sm text-muted-foreground py-4">Laadpunten zoeken...</p>}
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {alts?.map((a) => (
            <div key={a.ocm_id} className="bg-secondary/50 rounded-xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: powerColor(a.max_power_kw) }} data-testid={`alt-power-icon-${a.ocm_id}`}>
                  <Zap className="w-3.5 h-3.5 text-white" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-sm flex items-center gap-1.5 min-w-0" title={a.name}>
                    <span className="truncate">{a.name}</span>
                    {(a.operator === "Tesla" || /supercharger/i.test(a.name)) && <span data-testid={`tesla-badge-${a.ocm_id}`} className="shrink-0 text-[9px] font-bold bg-red-500/10 text-red-600 rounded-full px-1.5 py-0.5">TESLA</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {a.town} {a.operator ? `· ${a.operator}` : ""} · <span className="font-mono font-bold" style={{ color: powerColor(a.max_power_kw) }}>{a.max_power_kw || "?"} kW</span>{a.stalls != null ? ` · ${a.stalls} palen` : ""} · {a.distance_km} km {addrLoc ? "van het adres" : "verderop"}
                  </p>
                  {chargeMinutes(a.max_power_kw) != null && (() => {
                    const mins = chargeMinutes(a.max_power_kw);
                    const delta = stop.charge_minutes != null ? mins - stop.charge_minutes : null;
                    return (
                      <p className="text-xs font-mono mt-0.5" data-testid={`alt-charge-minutes-${a.ocm_id}`}>
                        <Clock className="w-3 h-3 inline -mt-0.5 mr-1 text-muted-foreground" />
                        ±{mins} min laden ({stop.arrival_soc || 15}% → {stop.charge_to_soc || 80}%)
                        {delta != null && Math.abs(delta) >= 3 && (
                          <span data-testid={`alt-charge-delta-${a.ocm_id}`} className={`ml-1.5 font-bold rounded-full px-1.5 py-0.5 text-[10px] ${delta < 0 ? "bg-green-500/15 text-green-700" : "bg-red-500/10 text-red-600"}`}>
                            {delta < 0 ? `${delta} min` : `+${delta} min`}
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>
              </div>
              <button data-testid={`choose-charger-${a.ocm_id}`} onClick={() => choose(a)}
                className="shrink-0 rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground text-xs font-semibold px-4 py-2 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                <Check className="w-3 h-3" /> Kies
              </button>
            </div>
          ))}
          {alts?.length === 0 && <p className="text-sm text-muted-foreground">{addrLoc ? `Geen laadpunten binnen 10 km van ${addrLoc.name} gevonden.` : "Geen laadpunten gevonden — probeer een andere zoekterm."}</p>}
        </div>
        {addrLoc && !loading && alts && (
          <div data-testid="alt-charger-custom-card" className="border border-dashed border-primary/40 rounded-xl p-3 space-y-2">
            <p className="text-xs font-semibold">Staat jouw laadpunt er niet bij?</p>
            <p className="text-[11px] text-muted-foreground">Vervang deze laadstop dan door een eigen laadpunt, precies op dit adres ({addrLoc.name}).</p>
            <div className="flex gap-2">
              <input data-testid="alt-charger-custom-name" value={customName} onChange={(e) => setCustomName(e.target.value)}
                placeholder="Naam, bijv. Tesla Supercharger"
                className="flex-1 min-w-0 bg-secondary rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#B5838D]" />
              <select data-testid="alt-charger-custom-kw" value={customKw} onChange={(e) => setCustomKw(e.target.value)}
                className="shrink-0 bg-secondary rounded-xl px-2 py-2 text-sm outline-none">
                {[50, 150, 250, 300, 400].map((kw) => <option key={kw} value={kw}>{kw} kW</option>)}
              </select>
            </div>
            <button data-testid="alt-charger-custom-submit" disabled={busy} onClick={chooseCustom}
              className="w-full rounded-full bg-primary text-primary-foreground text-xs font-bold px-4 py-2 disabled:opacity-50 active:scale-95 transition-[transform] duration-200">
              {busy ? "Vervangen..." : "Vervang door laadpunt op dit adres"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const crowdEstimate = (trip, day, stops, i, depOverride) => {
  const cs = stops[i];
  if (!cs || cs.fuel || !day?.date || !day?.drive_time_hours || !day?.distance_km) return null;
  const dep = depOverride || (trip?.departure_times || {})[day.day_number] || "09:00";
  const frac = Math.min((cs.km_from_day_start ?? day.distance_km / 2) / day.distance_km, 1);
  const driveMin = day.drive_time_hours * 60 * frac;
  const earlier = stops.slice(0, i).reduce((s, c) => s + (c.charge_minutes || 0), 0);
  const dt = new Date(`${day.date}T${dep.length === 5 ? dep : "09:00"}:00`);
  if (isNaN(dt.getTime())) return null;
  dt.setMinutes(dt.getMinutes() + driveMin + earlier);
  const h = dt.getHours(), wd = dt.getDay(), mo = dt.getMonth();
  let score = 0;
  if (h >= 10 && h < 17) score += 2;
  else if ((h >= 8 && h < 10) || (h >= 17 && h < 19)) score += 1;
  if (wd === 0 || wd === 5 || wd === 6) score += 1;
  if (mo === 6 || mo === 7) score += 1;
  if (cs.stalls != null) { if (cs.stalls <= 8) score += 1; else if (cs.stalls >= 16) score -= 1; }
  const level = score >= 4 ? "druk" : score >= 2 ? "gemiddeld" : "rustig";
  return { dt, level };
};

const CROWD_STYLE = {
  rustig: { label: "rustig", cls: "bg-emerald-500/15 text-emerald-700" },
  gemiddeld: { label: "gemiddeld", cls: "bg-amber-500/15 text-amber-700" },
  druk: { label: "druk verwacht", cls: "bg-red-500/15 text-red-600" },
};

const departureAdvice = (trip, day) => {
  const stops = day.charging_stops || [];
  if (!stops.some((s) => !s.fuel)) return null;
  const levelsFor = (dep) => stops.map((_, i) => crowdEstimate(trip, day, stops, i, dep)?.level).filter(Boolean);
  const score = (ls) => ls.reduce((s, l) => s + (l === "druk" ? 2 : l === "gemiddeld" ? 1 : 0), 0);
  const cur = (trip?.departure_times || {})[day.day_number] || "09:00";
  const curLevels = levelsFor(cur);
  const busyNow = curLevels.filter((l) => l === "druk").length;
  if (busyNow === 0) return null;
  const curScore = score(curLevels);
  const [ch, cm] = cur.split(":").map(Number);
  const curM = ch * 60 + cm;
  const candidates = [];
  for (let m = 330; m <= 720; m += 30) if (m !== curM) candidates.push(m);
  candidates.sort((a, b) => Math.abs(a - curM) - Math.abs(b - curM));
  let best = null;
  for (const m of candidates) {
    const t = `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
    const ls = levelsFor(t);
    const sc = score(ls);
    if (!best || sc < best.sc) best = { t, sc, busy: ls.filter((l) => l === "druk").length };
  }
  if (!best || best.sc >= curScore) return null;
  return { time: best.t, busyNow, busyThen: best.busy };
};

const DepartureAdvice = ({ trip, day, onRefresh }) => {
  const [applying, setApplying] = useState(false);
  const adv = departureAdvice(trip, day);
  if (!adv) return null;
  const apply = async () => {
    setApplying(true);
    try {
      await api.patch(`/trips/${trip.id}/departure`, { day_number: day.day_number, time: adv.time });
      toast.success(`Vertrektijd dag ${day.day_number} aangepast naar ${adv.time}`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };
  return (
    <div data-testid={`departure-advice-${day.day_number}`} className="bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5 flex items-center justify-between gap-3">
      <p className="text-xs text-amber-800">
        <Clock className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
        {adv.busyNow === 1 ? "1 laadstop valt" : `${adv.busyNow} laadstops vallen`} op een druk moment. Vertrek om <b className="font-mono">{adv.time}</b>{adv.busyThen === 0 ? " en je vermijdt de drukte." : ` — dan nog maar ${adv.busyThen} drukke stop${adv.busyThen > 1 ? "s" : ""}.`}
      </p>
      <button data-testid={`departure-advice-apply-${day.day_number}`} onClick={apply} disabled={applying}
        className="shrink-0 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-3.5 py-1.5 disabled:opacity-50 active:scale-95 transition-[transform,background-color] duration-200">
        {applying ? "..." : "Pas toe"}
      </button>
    </div>
  );
};

const ChargingStopItem = ({ trip, day, cs, i, readOnly, kmNow, onSelectPoint, onRefresh, onEmergency }) => {
  const [open, setOpen] = useState(false);
  const nw = !cs.fuel ? findNetwork(cs.operator) : null;
  const crowd = crowdEstimate(trip, day, day.charging_stops || [], i);
  const liveRemain = cs.km_from_day_start != null && kmNow != null && day.route_start_km != null
    ? day.route_start_km + cs.km_from_day_start - kmNow : null;
  const color = cs.fuel ? "#E76F51" : powerColor(cs.max_power_kw);
  const doDelete = async () => {
    if (!cs.manual && !(await confirmDialog("Deze geplande laadstop verwijderen? De batterijpercentages van deze dag worden herrekend zonder deze stop.", { title: "Laadstop verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      const { data } = await api.delete(`/trips/${trip.id}/days/${day.day_number}/charging/${i}`);
      const buffer = trip.charge_buffer_pct || 15;
      if (data?.arrival_soc != null && data.arrival_soc < buffer) {
        toast.warning(`Laadstop verwijderd — let op: aankomst nu ~${data.arrival_soc}%, onder je buffer van ${buffer}%`, { duration: 8000 });
      } else {
        toast.success(data?.arrival_soc != null ? `Laadstop verwijderd — aankomst nu ~${data.arrival_soc}%` : "Laadstop verwijderd");
      }
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid={`charging-stop-${day.day_number}-${i}`} className="bg-primary/5 rounded-xl overflow-hidden">
      <button type="button" data-testid={`charging-stop-toggle-${day.day_number}-${i}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-primary/10 transition-[background-color] duration-200">
        <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: color }} data-testid={`charging-stop-power-icon-${day.day_number}-${i}`}>
          {cs.fuel ? <Fuel className="w-3.5 h-3.5 text-white" /> : <Zap className="w-3.5 h-3.5 text-white" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="font-semibold text-sm truncate block leading-tight">
            {(cs.name || (cs.fuel ? "Tankstop" : "Laadstop")).split(",")[0]}
            {cs.preferred_network && <span data-testid={`preferred-network-${day.day_number}`} className="ml-1.5 text-[10px] font-bold bg-[#52B788]/15 text-[#2D6A4F] rounded-full px-1.5 py-0.5 align-middle">★</span>}
          </span>
          <span className="text-xs text-muted-foreground font-mono truncate block">
            {cs.fuel ? `±${cs.charge_minutes || 10} min tanken` : `±${cs.charge_minutes} min · ${cs.arrival_soc}→${cs.charge_to_soc}%`}
            {cs.km_from_day_start != null && <span className="hidden sm:inline">{` · na ${Math.round(cs.km_from_day_start)} km`}</span>}
            {liveRemain != null && (liveRemain > 0.5 ? ` · nog ${Math.round(liveRemain)} km` : " · ✓")}
          </span>
        </span>
        {!cs.fuel && cs.max_power_kw != null && (
          <span className="font-mono text-[11px] font-bold shrink-0 rounded-full px-2 py-0.5" style={{ color, background: `${color}1A` }}>{cs.max_power_kw} kW</span>
        )}
        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3">
          <div className="pl-10 space-y-1">
            {nw?.logo && (
              <img src={nw.logo} alt={nw.label} title={nw.label} data-testid={`charging-stop-logo-${day.day_number}-${i}`} className="h-5 max-w-[80px] object-contain object-left" />
            )}
            <p className="text-sm font-semibold leading-snug">
              {cs.name}
              {cs.preferred_network && <span className="ml-1.5 text-[10px] font-bold bg-[#52B788]/15 text-[#2D6A4F] rounded-full px-2 py-0.5 align-middle">★ voorkeursnetwerk</span>}
              {cs.manual && <span data-testid={`manual-stop-badge-${day.day_number}-${i}`} className="ml-1.5 text-[10px] font-bold bg-[#3A6EA5]/10 text-[#3A6EA5] rounded-full px-2 py-0.5 align-middle">handmatig</span>}
            </p>
            {(cs.town || cs.operator) && (
              <p className="text-xs text-muted-foreground">{cs.town}{cs.town && cs.operator && " · "}{cs.operator}</p>
            )}
            {!cs.fuel && cs.max_power_kw == null && <p className="text-xs text-muted-foreground font-mono">vermogen onbekend</p>}
            {!cs.fuel && cs.connectors?.length > 0 && (
              <p className="text-xs text-muted-foreground">{cs.connectors.map((c) => `${c.type} (${c.power_kw} kW)`).join(", ")}</p>
            )}
            {crowd && (
              <p data-testid={`crowd-estimate-${day.day_number}-${i}`} className="text-xs flex items-center gap-1.5 flex-wrap">
                <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">
                  Aankomst ~{crowd.dt.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                  {cs.stalls != null ? ` · ${cs.stalls} palen` : ""} · verwachte drukte:
                </span>
                <span className={`font-bold rounded-full px-2 py-0.5 text-[10px] ${CROWD_STYLE[crowd.level].cls}`}>{CROWD_STYLE[crowd.level].label}</span>
              </p>
            )}
            {crowd?.level === "druk" && (
              <p data-testid={`crowd-warning-${day.day_number}-${i}`} className="text-xs text-amber-800 bg-amber-500/10 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Rond dit tijdstip is het hier vaak druk — vertrek wat vroeger, of kies via "Vervang" een locatie met meer laadpalen. Dit is een inschatting, geen live data.</span>
              </p>
            )}
            {!cs.fuel && cs.cost_eur != null && (
              <p className="text-xs font-mono text-primary">~{cs.energy_kwh} kWh · €{cs.cost_eur.toFixed(2)}</p>
            )}
            {cs.km_from_day_start != null && (
              <p className="text-xs font-mono text-[#3A6EA5]" data-testid={`charging-stop-km-${day.day_number}-${i}`}>
                na {Math.round(cs.km_from_day_start)} km rijden vandaag
                {liveRemain != null && (liveRemain > 0.5 ? ` · nog ${Math.round(liveRemain)} km (live)` : " · gepasseerd ✓")}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pl-10 mt-2.5 pt-2.5 border-t border-border/40">
            {cs.lat && (
              <button data-testid={`show-on-map-${day.day_number}-${i}`} onClick={() => onSelectPoint({ lat: cs.lat, lon: cs.lon })}
                className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1 transition-[color] duration-200">
                <MapPin className="w-3 h-3" /> Kaart
              </button>
            )}
            {cs.lat && !readOnly && !cs.fuel && <AlternativeChargers stop={cs} tripId={trip.id} dayNumber={day.day_number} stopIndex={i} vehicle={trip.vehicle} onRefresh={onRefresh} />}
            {!cs.fuel && (
              <ChargeCalcDialog trip={trip} prefill={{ kw: cs.max_power_kw || 50, minutes: cs.charge_minutes || 30, soc: cs.arrival_soc }}>
                <button data-testid={`charge-calc-button-${day.day_number}-${i}`}
                  className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1 transition-[color] duration-200">
                  <Calculator className="w-3 h-3" /> Rekenaar
                </button>
              </ChargeCalcDialog>
            )}
            {!readOnly && !cs.fuel && (
              <button data-testid={`delete-charging-stop-${day.day_number}-${i}`} onClick={doDelete}
                className="text-xs font-semibold text-muted-foreground hover:text-red-600 flex items-center gap-1 transition-[color] duration-200">
                <Trash2 className="w-3 h-3" /> Verwijder
              </button>
            )}
            {cs.lat && !readOnly && !cs.fuel && (
              <button data-testid={`emergency-stop-${day.day_number}-${i}`} onClick={onEmergency}
                className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 transition-[color] duration-200">
                <AlertTriangle className="w-3 h-3" /> Bezet/defect
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StayAlternatives = ({ tripId, dayNumber, place, onRefresh }) => {
  const [alts, setAlts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai");
  const [ownName, setOwnName] = useState("");
  const [ownUrl, setOwnUrl] = useState("");
  const [ownPrice, setOwnPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setAlts(null);
    try {
      const { data } = await api.post(`/trips/${tripId}/days/${dayNumber}/stay/alternatives`);
      setAlts(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const choose = async (s) => {
    try {
      await api.patch(`/trips/${tripId}/days/${dayNumber}/stay`, s);
      toast.success("Verblijf vervangen!");
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveOwn = async () => {
    const name = ownName.trim();
    if (!name) { toast.error("Geef je verblijf een naam"); return; }
    let url = ownUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    const price = ownPrice === "" ? null : parseFloat(ownPrice);
    setSaving(true);
    try {
      await api.patch(`/trips/${tripId}/days/${dayNumber}/stay`, {
        name, type: "eigen boeking", description: "Door jou opgegeven verblijf", url: url || null,
        price_eur: price, price_indication: price != null ? `€ ${price.toFixed(0)} per nacht` : null,
      });
      toast.success(price != null ? `${name} ingesteld — € ${price.toFixed(0)}/nacht telt mee in je dagbudget` : `${name} ingesteld als jouw verblijf!`);
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setTab("ai"); setOwnName(""); setOwnUrl(""); setOwnPrice(""); load(); } }}>
      <DialogTrigger asChild>
        <button data-testid={`stay-alternatives-button-${dayNumber}`} className="text-xs font-semibold text-[#3A6EA5] hover:text-[#2A5580] flex items-center gap-1 transition-[color] duration-200">
          <RefreshCw className="w-3 h-3" /> Vervang
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Verblijf vervangen{place ? ` in ${place}` : ""}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5 -mt-1 mb-1">
          <button type="button" data-testid="stay-tab-ai" onClick={() => setTab("ai")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${tab === "ai" ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-background text-muted-foreground border-border hover:border-[#3A6EA5]/50"}`}>
            Zoek alternatieven
          </button>
          <button type="button" data-testid="stay-tab-own" onClick={() => setTab("own")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors duration-200 ${tab === "own" ? "bg-[#3A6EA5] text-white border-[#3A6EA5]" : "bg-background text-muted-foreground border-border hover:border-[#3A6EA5]/50"}`}>
            Eigen verblijf
          </button>
        </div>
        {tab === "ai" && (
          <>
            {loading && <p className="text-sm text-muted-foreground py-4 animate-pulse-soft">AI zoekt alternatieven...</p>}
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {alts?.map((s, i) => (
                <div key={i} className="bg-secondary/50 rounded-xl p-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">
                      <a href={infoUrl(s.name, place)} target="_blank" rel="noreferrer" title="Meer info op internet"
                        className="hover:text-[#3A6EA5] underline decoration-dotted underline-offset-2 transition-[color] duration-200">{s.name}</a>
                      <span className="text-xs font-normal text-muted-foreground"> ({s.type})</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                    {s.price_indication && <p className="text-xs font-mono text-muted-foreground mt-1">{s.price_indication}</p>}
                  </div>
                  <button data-testid={`choose-stay-${i}`} onClick={() => choose(s)}
                    className="shrink-0 rounded-full bg-[#3A6EA5] hover:bg-[#2A5580] text-white text-xs font-semibold px-4 py-2 flex items-center gap-1 active:scale-95 transition-[transform,background-color] duration-200">
                    <Check className="w-3 h-3" /> Kies
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        {tab === "own" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Zelf al iets geboekt? Vul de naam in en plak de link van <strong>Booking.com</strong> of <strong>Airbnb</strong> — dan open je je boeking later met één tik.
            </p>
            <Input data-testid="own-stay-name-input" value={ownName} onChange={(e) => setOwnName(e.target.value)}
              placeholder="Naam van je verblijf (bijv. Hotel Bellevue)" className="rounded-xl" />
            <Input data-testid="own-stay-url-input" value={ownUrl} onChange={(e) => setOwnUrl(e.target.value)}
              placeholder="Link van Booking.com of Airbnb (optioneel)" className="rounded-xl" />
            <Input data-testid="own-stay-price-input" type="number" min="0" step="0.01" value={ownPrice} onChange={(e) => setOwnPrice(e.target.value)}
              placeholder="Prijs per nacht in € (optioneel — telt mee in je dagbudget)" className="rounded-xl font-mono" />
            <Button data-testid="own-stay-save-button" onClick={saveOwn} disabled={saving || !ownName.trim()}
              className="rounded-full w-full bg-[#3A6EA5] hover:bg-[#2A5580] active:scale-95 transition-[transform,background-color] duration-200">
              {saving ? "Opslaan..." : "Gebruik dit verblijf"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const gmNearestIdx = (geometry, pt, from = 0) => {
  let best = from;
  let bestD = Infinity;
  for (let i = from; i < geometry.length; i++) {
    const d = (geometry[i][0] - pt.lat) ** 2 + (geometry[i][1] - pt.lon) ** 2;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
};

export const gmapsUrl = (trip, day, navigate = false) => {
  const stops = (day.charging_stops || []).filter((cs) => cs.lat);
  let points = stops.map((cs) => ({ lat: cs.lat, lon: cs.lon, idx: 0 }));
  const geometry = trip?.route?.geometry || [];
  if (geometry.length > 2 && day.start?.lat != null && day.end?.lat != null) {
    const s = gmNearestIdx(geometry, day.start);
    const e = gmNearestIdx(geometry, day.end, s);
    if (e > s + 2) {
      const seg = geometry.slice(s, e + 1);
      points = stops.map((cs) => ({ lat: cs.lat, lon: cs.lon, idx: gmNearestIdx(seg, cs) }));
      const free = Math.min(9 - points.length, 6);
      for (let i = 1; i <= free; i++) {
        const idx = Math.round((i * (seg.length - 1)) / (free + 1));
        if (points.some((p) => Math.abs(p.idx - idx) < (seg.length - 1) * 0.06)) continue;
        points.push({ lat: seg[idx][0], lon: seg[idx][1], idx });
      }
      points.sort((a, b) => a.idx - b.idx);
    }
  }
  const wp = points.slice(0, 9).map((p) => `${(+p.lat).toFixed(5)},${(+p.lon).toFixed(5)}`).join("|");
  const avoid = [
    ...(trip?.avoid_toll ? ["tolls"] : []),
    ...(trip?.avoid_ferry ? ["ferries"] : []),
    ...(trip?.route_preference === "nationaal" ? ["highways"] : []),
  ].join(",");
  return `https://www.google.com/maps/dir/?api=1&origin=${day.start.lat},${day.start.lon}&destination=${day.end.lat},${day.end.lon}${wp ? `&waypoints=${encodeURIComponent(wp)}` : ""}&travelmode=driving${avoid ? `&avoid=${encodeURIComponent(avoid)}` : ""}${navigate ? "&dir_action=navigate" : ""}`;
};

export const stayLinks = (trip, day) => {
  const loc = day.end?.name || "";
  const checkin = day.date;
  const d = new Date(day.date);
  d.setDate(d.getDate() + 1);
  const checkout = d.toISOString().slice(0, 10);
  const a = trip.travel_party?.adults || 2;
  const c = trip.travel_party?.children || 0;
  const aid = process.env.REACT_APP_BOOKING_AID;
  return {
    booking: `https://www.booking.com/searchresults.nl.html?ss=${encodeURIComponent(loc)}&checkin=${checkin}&checkout=${checkout}&group_adults=${a}&group_children=${c}&no_rooms=1${aid ? `&aid=${aid}` : ""}`,
    airbnb: `https://www.airbnb.nl/s/${encodeURIComponent(loc)}/homes?checkin=${checkin}&checkout=${checkout}&adults=${a}&children=${c}`,
  };
};

export const Timeline = ({ trip, onSelectPoint, onRefresh, readOnly, dayEntries }) => {
  const [gpsPos, setGpsPos] = useState(null);
  useEffect(() => {
    if (readOnly || !isTripToday(trip) || !navigator.geolocation) return;
    let watchId = null;
    let cancelled = false;
    const start = () => {
      watchId = navigator.geolocation.watchPosition(
        (p) => setGpsPos({ lat: p.coords.latitude, lon: p.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 20000 }
      );
    };
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((st) => { if (!cancelled && st.state === "granted") start(); }).catch(() => {});
    }
    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [trip.id]);
  const routeInfo = useMemo(() => buildRouteInfo(trip.route), [trip.route]);
  const days = trip.days || [];
  const isMoto = trip.travel_mode === "moto";
  const topCurveDay = useMemo(() => {
    if (!(isMoto || trip.route_preference === "bochtig")) return null;
    const cands = (trip.days || []).filter((d) => d.type !== "stay" && d.curviness != null && (d.curve_stars || 0) >= 2);
    if (cands.length < 2) return null;
    return cands.reduce((a, b) => (b.curviness > a.curviness ? b : a)).day_number;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.days, isMoto, trip.route_preference]);
  const firstDriveNum = useMemo(() => (days.find((d) => d.type !== "stay" && (d.distance_km || 0) > 0) || {}).day_number, [days]);
  const defaultSocFor = (day) => (day.day_number === firstDriveNum ? (trip.start_battery_pct || 100) : 100);
  const todayDriveDay = useMemo(() => days.find((d) => d.date === localToday() && d.type !== "stay" && (d.distance_km || 0) > 0), [days]);
  const kmNow = useMemo(() => kmOnRoute(routeInfo, gpsPos, 5, dayWindow(todayDriveDay)), [routeInfo, gpsPos, todayDriveDay]);
  const [collapsed, setCollapsed] = useState(() => {
    const today = localToday();
    if (days.some((d) => d.date === today)) return Object.fromEntries(days.map((d) => [d.day_number, d.date !== today]));
    return {};
  });
  useEffect(() => {
    const todayDay = days.find((d) => d.date === localToday());
    if (!todayDay || todayDay.day_number === 1) return;
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="timeline-day-${todayDay.day_number}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggleDay = (n) => setCollapsed((c) => ({ ...c, [n]: !c[n] }));

  const deleteDay = async (day) => {
    if (!(await confirmDialog(`Dag ${day.day_number} (${fmtDate(day.date)}) verwijderen? Je trip wordt 1 dag korter; de dagen erna schuiven een dag naar voren.`, { title: "Dag verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/trips/${trip.id}/days/${day.day_number}`);
      toast.success("Dag verwijderd — planning direct aangepast");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const shortenStay = async (day) => {
    const place = (resolveName(day.end) || day.end?.name || "deze plek").split(",")[0];
    if (!(await confirmDialog(`Eén dag minder in ${place}? Je trip wordt 1 dag korter; de dagen erna schuiven een dag naar voren.`, { title: "Dag korter", confirmLabel: "1 dag korter" }))) return;
    try {
      await api.delete(`/trips/${trip.id}/days/${day.day_number}`);
      toast.success(`1 dag minder in ${place} — planning direct aangepast`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteBlock = async (day, run) => {
    const first = day.day_number - (run.pos - 1);
    if (!(await confirmDialog(`Heel verblijf verwijderen (${run.total} dagen)? Je trip wordt ${run.total} dagen korter; de dagen erna schuiven naar voren.`, { title: "Verblijf verwijderen", confirmLabel: "Verwijderen", destructive: true }))) return;
    try {
      await api.delete(`/trips/${trip.id}/days/${first}?count=${run.total}`);
      toast.success(`Verblijf verwijderd (${run.total} dagen) — planning direct aangepast`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteOvernight = async (day) => {
    const place = (resolveName(day.end) || day.end?.name || "deze plek").split(",")[0];
    if (!(await confirmDialog(`Overnachting in ${place} schrappen? Je rijdt dan door zonder deze stop — de volledige planning wordt direct opnieuw berekend en je trip wordt korter.`, { title: "Overnachting schrappen", confirmLabel: "Schrappen", destructive: true }))) return;
    try {
      const { data } = await api.delete(`/trips/${trip.id}/days/${day.day_number}/overnight`);
      toast.success(`Overnachting geschrapt${data?.note ? ` (${data.note})` : ""} — planning wordt opnieuw berekend!`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteLeg = async (day, di) => {
    const prev = days[di - 1];
    const from = (resolveName(day.start) || "").split(",")[0];
    const to = (resolveName(day.end) || "").split(",")[0];
    const prevFrom = (resolveName(prev.start) || "").split(",")[0];
    if (!(await confirmDialog(`Traject ${from} → ${to} schrappen? De overnachting in ${from} vervalt — je rijdt dan in één dag van ${prevFrom} naar ${to}. De volledige planning wordt opnieuw berekend.`, { title: "Traject schrappen", confirmLabel: "Schrappen", destructive: true }))) return;
    try {
      const { data } = await api.delete(`/trips/${trip.id}/days/${prev.day_number}/overnight`);
      toast.success(`Traject geschrapt${data?.note ? ` (${data.note})` : ""} — planning wordt opnieuw berekend!`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteBogus = async (day) => {
    const from = (resolveName(day.start) || "").split(",")[0];
    const to = (resolveName(day.end) || "").split(",")[0];
    if (!(await confirmDialog(`Dag ${day.day_number} (${from} → ${to}) rijdt wég van je eindbestemming en hoort niet in de route. Deze dag en alle dagen erna verwijderen en de route herstellen?`, { title: "Route herstellen", confirmLabel: "Herstel route", destructive: true }))) return;
    try {
      await api.delete(`/trips/${trip.id}/days/${day.day_number}`);
      toast.success("Route hersteld — planning wordt opnieuw berekend");
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const allCollapsed = days.length > 0 && days.every((d) => collapsed[d.day_number]);
  const setAll = (v) => setCollapsed(Object.fromEntries(days.map((d) => [d.day_number, v])));

  const samePoint = (a, b) => a && b && Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lon - b.lon) < 1e-6;
  const nearPt = (a, b) => a && b && a.lat != null && b.lat != null && Math.abs(a.lat - b.lat) < 0.05 && Math.abs(a.lon - b.lon) < 0.05;
  const isBogusEscape = (day, di) => !trip.round_trip && di > 0 && day.type !== "stay" && (day.distance_km || 0) >= 1
    && nearPt(day.start, trip.destination) && !nearPt(day.end, trip.destination)
    && days.slice(0, di).some((d) => nearPt(d.end, trip.destination));
  const resolveName = (pt) => {
    if (!pt) return "";
    if (samePoint(pt, trip.origin)) return trip.origin.name;
    if (samePoint(pt, trip.destination)) return trip.destination.name;
    return pt.name;
  };
  let arriveIdx = -1;
  let turnIdx = -1;
  days.forEach((d, i) => {
    if (samePoint(d.end, trip.destination)) {
      if (arriveIdx === -1) arriveIdx = i;
      turnIdx = i;
    }
  });
  const stayRuns = {};
  for (let i = 0; i < days.length; ) {
    if (days[i].type === "stay") {
      let j = i;
      while (j < days.length && days[j].type === "stay") j++;
      for (let k = i; k < j; k++) stayRuns[k] = { pos: k - i + 1, total: j - i };
      i = j;
    } else i++;
  }
  const dayChips = (di) => {
    const chips = [];
    if (di === 0) chips.push({ key: "vertrek", label: "Start", cls: "bg-[#2D6A4F]/10 text-[#2D6A4F]", Icon: Flag });
    if (di === arriveIdx && days[di].type !== "stay" && di !== days.length - 1) chips.push({ key: "bestemming", label: "Bestemming", cls: "bg-[#B5838D]/15 text-[#B5838D]", Icon: MapPin });
    if (trip.round_trip && di > turnIdx && di < days.length - 1) chips.push({ key: "retour", label: "Retour", cls: "bg-amber-500/10 text-amber-700", Icon: Undo2 });
    if (di === days.length - 1 && days.length > 1) chips.push({ key: "eindpunt", label: trip.round_trip ? (samePoint(days[di].end, trip.origin) ? "Thuiskomst — eindpunt" : "Eindpunt") : "Eindpunt", cls: "bg-[#3A6EA5]/10 text-[#3A6EA5]", Icon: Home });
    return chips;
  };

  const emergency = async (dayNumber, i) => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/days/${dayNumber}/charging/${i}/emergency`);
      toast.success(`Plan aangepast: nieuwe lader ${data.name} (${data.max_power_kw || "?"} kW)`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const today = localToday();

  return (
    <div className="space-y-6" data-testid="trip-timeline">
      {trip.generation_warning && (
        <div className="bg-amber-100 text-amber-900 rounded-2xl p-4 text-sm" data-testid="generation-warning">{trip.generation_warning}</div>
      )}
      {(days.length > 1 || (!readOnly && ((trip.planned_stays || []).length > 0 || (trip.planned_stays_return || []).length > 0))) && (
        <div className="flex items-center justify-between gap-3 -mb-2 flex-wrap">
          {!readOnly && ((trip.planned_stays || []).length > 0 || (trip.planned_stays_return || []).length > 0)
            ? <RouteStopsDialog trip={trip} onRefresh={onRefresh} /> : <span />}
          {days.length > 1 && (
            <button data-testid="toggle-all-days-button" type="button" onClick={() => setAll(!allCollapsed)}
              className="text-xs font-semibold text-muted-foreground hover:text-primary flex items-center gap-1.5 transition-[color] duration-200">
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${allCollapsed ? "-rotate-90" : ""}`} />
              {allCollapsed ? "Alles uitklappen" : "Alles inklappen"}
            </button>
          )}
        </div>
      )}
      {days.map((day, di) => (
        <motion.div
          key={day.day_number}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: di * 0.08 }}
          className="relative pl-12"
          data-testid={`timeline-day-${day.day_number}`}
        >
          <div className={`absolute left-0 top-1 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-sm shadow-md ${day.date === today ? "ring-4 ring-[#74C69D]/40" : ""}`}>
            {day.day_number}
          </div>
          {di < days.length - 1 && <div className="timeline-line" />}

          <div className={`bg-card rounded-2xl p-6 ${day.date === today ? "ring-2 ring-[#74C69D] shadow-[0_8px_32px_rgba(45,106,79,0.16)]" : "shadow-[0_8px_32px_rgba(31,38,59,0.06)]"}`}>
            <div data-testid={`day-toggle-${day.day_number}`} onClick={() => toggleDay(day.day_number)} className="cursor-pointer select-none">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{fmtDate(day.date)}</p>
              {day.date === today && (
                <span data-testid={`day-chip-vandaag-${day.day_number}`}
                  className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 bg-primary text-primary-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#74C69D] animate-pulse" /> Vandaag
                </span>
              )}
              {dayChips(di).map((c) => (
                <span key={c.key} data-testid={`day-chip-${c.key}-${day.day_number}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5 ${c.cls}`}>
                  <c.Icon className="w-3 h-3" /> {c.label}
                </span>
              ))}
              <div className="ml-auto flex items-center gap-0.5">
                {!readOnly && isBogusEscape(day, di) && (
                  <button data-testid={`delete-bogus-day-${day.day_number}`} title="Deze dag hoort niet in de route — verwijder en herstel de route"
                    onClick={(e) => { e.stopPropagation(); deleteBogus(day); }}
                    className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {!readOnly && !isBogusEscape(day, di) && day.type !== "stay" && (day.distance_km || 0) >= 1 && di > 0 && days[di - 1]?.type !== "stay" && (
                  <button data-testid={`delete-leg-${day.day_number}`} title="Schrap dit traject — de vorige dag rijdt dan door naar dit eindpunt"
                    onClick={(e) => { e.stopPropagation(); deleteLeg(day, di); }}
                    className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {!readOnly && !isBogusEscape(day, di) && day.type !== "stay" && (day.distance_km || 0) >= 1
                  && !(di > 0 && days[di - 1]?.type !== "stay")
                  && day.end?.lat != null && di < days.length - 1 && di !== arriveIdx
                  && !(trip.round_trip && samePoint(day.end, trip.destination))
                  && !((trip.status === "active" || trip.was_started) && day.date < today) && (
                  <button data-testid={`delete-overnight-header-${day.day_number}`} title="Schrap deze tussenstop — je rijdt door zonder deze overnachting"
                    onClick={(e) => { e.stopPropagation(); deleteOvernight(day); }}
                    className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {!readOnly && (day.type === "stay" || (day.distance_km || 0) < 1) && days.length > 1 && (
                  <button data-testid={`delete-day-${day.day_number}`} title="Verwijder deze dag (trip wordt 1 dag korter)"
                    onClick={(e) => { e.stopPropagation(); deleteDay(day); }}
                    className="p-1.5 rounded-full hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-[background-color,color] duration-200">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${collapsed[day.day_number] ? "-rotate-90" : ""}`} />
              </div>
            </div>
            {day.type === "stay" ? (
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2 flex-wrap">
                <Palmtree className="w-5 h-5 text-accent" /> Verblijf in {resolveName(day.end)}
                {!readOnly && di === 0 && <EditEndpointDialog trip={trip} kind="origin" current={resolveName(day.end)} onRefresh={onRefresh} />}
                {stayRuns[di]?.total > 1 && <span className="text-sm font-normal text-muted-foreground">&nbsp;— dag {stayRuns[di].pos} van {stayRuns[di].total}</span>}
              </h3>
            ) : (
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-1.5 flex-wrap">
                <span>{resolveName(day.start)}</span>
                {!readOnly && di === 0 && <EditEndpointDialog trip={trip} kind="origin" current={resolveName(day.start)} onRefresh={onRefresh} />}
                <span className="text-muted-foreground">→</span>
                <span>{resolveName(day.end)}</span>
                {!readOnly && di === arriveIdx && <EditEndpointDialog trip={trip} kind="destination" current={resolveName(day.end)} onRefresh={onRefresh} />}
                {!readOnly && di === days.length - 1 && di !== 0 && di !== arriveIdx && !samePoint(day.end, trip.destination) && (
                  samePoint(day.end, trip.origin)
                    ? <EditEndpointDialog trip={trip} kind="origin" day={day} current={resolveName(day.end)} onRefresh={onRefresh} />
                    : <EditEndpointDialog trip={trip} kind="return_end" day={day} current={resolveName(day.end)} onRefresh={onRefresh} />
                )}
                {!readOnly && di !== arriveIdx && di < days.length - 1 && !samePoint(day.end, trip.destination) && !samePoint(day.end, trip.origin)
                  && !((trip.status === "active" || trip.was_started) && day.date < today) && (
                  <EditEndpointDialog trip={trip} kind="stopover" day={day} current={resolveName(day.end)} onRefresh={onRefresh} />
                )}
              </h3>
            )}
            </div>
            {collapsed[day.day_number] ? (
              day.type !== "stay" && (
                <p data-testid={`day-summary-${day.day_number}`} className="text-sm text-muted-foreground font-mono">
                  {day.distance_km} km · ±{day.drive_time_hours}u{(day.charging_stops || []).length ? ` · ${day.charging_stops.length} ${isMoto ? "tankstop" : "laadstop"}${day.charging_stops.length > 1 ? "s" : ""}` : ""}{day.stay ? <> · {day.stay.type === "eigen boeking" && <Lock data-testid={`day-summary-lock-${day.day_number}`} className="inline w-3 h-3 -mt-0.5 mr-0.5 text-emerald-600" />}{day.stay.name}</> : ""}
                </p>
              )
            ) : (
            <>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
              {day.type !== "stay" && (
                <>
                  <span className="flex items-center gap-1.5"><Route className="w-4 h-4 text-primary" /><span className="font-mono">{day.distance_km} km</span></span>
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /><span className="font-mono">±{day.drive_time_hours}u rijden</span></span>
                  {day.curve_stars != null && (isMoto || trip.route_preference === "bochtig") && (
                    <span data-testid={`curve-score-${day.day_number}`}
                      title={`Bochtigheid: ${day.curviness}°/km — hoe meer sterren, hoe bochtiger (en leuker) de etappe`}
                      className="flex items-center gap-1 text-amber-500">
                      <span className="tracking-tight text-base leading-none" aria-label={`${day.curve_stars} van 5 sterren bochtigheid`}>
                        {"★".repeat(day.curve_stars)}<span className="text-muted-foreground/40">{"★".repeat(5 - day.curve_stars)}</span>
                      </span>
                      <span className="text-xs font-semibold text-muted-foreground">bochtig</span>
                    </span>
                  )}
                  {topCurveDay === day.day_number && (
                    <span data-testid={`fun-stage-badge-${day.day_number}`}
                      title="De bochtigste dag van je trip — de leukste etappe!"
                      className="flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-600 px-2.5 py-0.5 text-xs font-bold">
                      <Trophy className="w-3.5 h-3.5" /> leukste etappe
                      <StageShareButton trip={trip} day={day} testId={`share-stage-timeline-${day.day_number}`} />
                    </span>
                  )}
                  <DepartureTime trip={trip} day={day} readOnly={readOnly} onRefresh={onRefresh} />
                  {!isMoto && <DayBattery trip={trip} day={day} defaultSoc={defaultSocFor(day)} readOnly={readOnly} onRefresh={onRefresh} />}
                  {!isMoto && (
                  <span className={`flex items-center gap-1.5 ${day.arrival_soc != null && day.arrival_soc < (trip.charge_buffer_pct || 15) ? "text-red-600 font-semibold" : ""}`}>
                    {day.arrival_soc != null && day.arrival_soc < (trip.charge_buffer_pct || 15)
                      ? <AlertTriangle className="w-4 h-4 text-red-600" title="Aankomst onder je buffer — voeg een laadstop toe" />
                      : <BatteryCharging className="w-4 h-4 text-primary" />}
                    <span className="font-mono" data-testid={`arrival-soc-${day.day_number}`}>aankomst ~{day.arrival_soc}%</span>
                  </span>
                  )}
                </>
              )}
              {day.date === today ? (
                <LiveWeatherToday day={day} />
              ) : day.weather && (
                <span className="flex items-center gap-1.5" data-testid={`weather-day-${day.day_number}`}><ThermometerSun className="w-4 h-4 text-amber-500" /><span className="font-mono">{day.weather.temp_max}°C</span><span className="text-xs">{day.weather.description}</span></span>
              )}
              {day.type !== "stay" && (
                <a data-testid={`gmaps-day-${day.day_number}`} href={gmapsUrl(trip, day)} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-primary hover:text-[#1B4332] font-semibold text-xs transition-[color] duration-200">
                  <ExternalLink className="w-3.5 h-3.5" /> Google Maps
                </a>
              )}
              {day.end?.name && (
                <a data-testid={`place-info-${day.day_number}`} href={infoUrl((resolveName(day.end) || day.end.name).split(",")[0], "bezienswaardigheden info")} target="_blank" rel="noreferrer"
                  title={`Meer info en bezienswaardigheden over ${(resolveName(day.end) || day.end.name).split(",")[0]}`}
                  className="flex items-center gap-1 text-[#B5838D] hover:text-[#8E5D67] font-semibold text-xs transition-[color] duration-200">
                  <ExternalLink className="w-3.5 h-3.5" /> Ontdek {(resolveName(day.end) || day.end.name).split(",")[0]}
                </a>
              )}
            </div>
            {!readOnly && day.type === "stay" && stayRuns[di]?.total > 1 && (
              <button data-testid={`delete-stay-block-${day.day_number}`} onClick={() => deleteBlock(day, stayRuns[di])}
                className="mb-3 text-xs font-semibold text-red-500 hover:text-red-600 flex items-center gap-1.5 transition-[color] duration-200">
                <Trash2 className="w-3.5 h-3.5" /> Verwijder heel verblijf ({stayRuns[di].total} dagen)
              </button>
            )}
            {!isMoto && day.type !== "stay" && day.weather && day.weather.range_factor < 1 && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5 inline-block mb-3">Bereik -{Math.round((1 - day.weather.range_factor) * 100)}% door verwachte temperatuur — laadstops zijn hierop aangepast</p>
            )}
            {day.type !== "stay" && <VignetteDayNote dayEntries={dayEntries} dayNumber={day.day_number} />}
            {isMoto && day.type !== "stay" && day.weather && /regen|bui|onweer|hagel|sneeuw|storm/i.test(day.weather.description || "") && (
              <p data-testid={`rain-warning-${day.day_number}`} className="text-xs font-bold text-white bg-[#3A6EA5] rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 mb-3">🌧️ {day.weather.description} verwacht — regenpak bij de hand!</p>
            )}

            {!isMoto && day.type !== "stay" && (day.distance_km || 0) > 0 && (
              <SocChart trip={trip} day={day} defaultSoc={defaultSocFor(day)}
                liveKm={day.date === today && kmNow != null && day.route_start_km != null ? kmNow - day.route_start_km : null} />
            )}

            {((day.charging_stops || []).length > 0 || (!isMoto && !readOnly && day.type !== "stay" && (day.distance_km || 0) > 0)) && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary">{isMoto ? "Tankstops" : "Laadstops"}</p>
                  {!isMoto && !readOnly && day.type !== "stay" && (day.distance_km || 0) > 0 && (
                    <AddCharger trip={trip} day={day} onRefresh={onRefresh} />
                  )}
                </div>
                {!isMoto && !readOnly && <DepartureAdvice trip={trip} day={day} onRefresh={onRefresh} />}
                {(day.charging_stops || []).map((cs, i) => (
                  <ChargingStopItem key={i} trip={trip} day={day} cs={cs} i={i} readOnly={readOnly} kmNow={kmNow}
                    onSelectPoint={onSelectPoint} onRefresh={onRefresh} onEmergency={() => emergency(day.day_number, i)} />
                ))}
              </div>
            )}

            {day.stay && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#3A6EA5]">Overnachting</p>
                  {!readOnly && (
                    <div className="flex items-center gap-3">
                      <ExtraNightsDialog trip={trip} day={day} onRefresh={onRefresh} />
                      {day.type === "stay" && (
                        <button data-testid={`shorten-stay-${day.day_number}`} onClick={() => shortenStay(day)}
                          title="Blijf hier één nacht minder — je trip wordt 1 dag korter en de dagen erna schuiven naar voren"
                          className="text-xs font-semibold text-[#3A6EA5] hover:text-[#2d5884] flex items-center gap-1 transition-[color] duration-200">
                          <Minus className="w-3.5 h-3.5" /> Dag korter
                        </button>
                      )}
                      <StayAlternatives tripId={trip.id} dayNumber={day.day_number} place={(day.end?.name || "").split(",")[0]} onRefresh={onRefresh} />
                      {day.type !== "stay" && di < days.length - 1 && !(trip.round_trip && samePoint(day.end, trip.destination))
                        && !((trip.status === "active" || trip.was_started) && day.date < today) && (
                        <button data-testid={`delete-overnight-${day.day_number}`} onClick={() => deleteOvernight(day)}
                          title="Schrap deze overnachting en rij door — de planning wordt direct opnieuw berekend"
                          className="text-xs font-semibold text-red-500 hover:text-red-600 flex items-center gap-1 transition-[color] duration-200">
                          <Trash2 className="w-3.5 h-3.5" /> Schrappen
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="bg-[#3A6EA5]/5 rounded-xl p-4 flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#3A6EA5] flex items-center justify-center shrink-0 mt-0.5">
                    <BedDouble className="w-4 h-4 text-white" />
                  </span>
                  <div>
                    <p className="font-semibold text-sm">
                      <a data-testid={`stay-info-link-${day.day_number}`} href={day.stay.url || infoUrl(day.stay.name, (day.end?.name || "").split(",")[0])} target="_blank" rel="noreferrer"
                        title={day.stay.url ? "Open je boeking" : "Meer info op internet"}
                        className="hover:text-[#3A6EA5] underline decoration-dotted underline-offset-2 transition-[color] duration-200">{day.stay.name}</a>
                      <span className="text-xs font-normal text-muted-foreground"> ({day.stay.type})</span>
                      {day.stay.type === "eigen boeking" && (
                        <span data-testid={`stay-locked-${day.day_number}`} title="Eigen boeking — dit verblijf staat definitief vast"
                          className="inline-flex items-center gap-1 ml-1.5 align-middle text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                          <Lock className="w-3 h-3" /> bevestigd
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{day.stay.description}</p>
                    {day.stay.price_indication && <p className="text-xs font-mono text-muted-foreground mt-1">{day.stay.price_indication}</p>}
                    {day.stay.type !== "eigen boeking" && (
                      <div className="flex gap-2 mt-2.5">
                        <a data-testid={`booking-link-${day.day_number}`} href={stayLinks(trip, day).booking} target="_blank" rel="noreferrer"
                          className="text-[11px] font-bold bg-[#003580] text-white rounded-full px-3 py-1.5 hover:opacity-85 transition-opacity duration-200">
                          Boek op Booking.com
                        </a>
                        <a data-testid={`airbnb-link-${day.day_number}`} href={stayLinks(trip, day).airbnb} target="_blank" rel="noreferrer"
                          className="text-[11px] font-bold bg-[#FF385C] text-white rounded-full px-3 py-1.5 hover:opacity-85 transition-opacity duration-200">
                          Bekijk op Airbnb
                        </a>
                      </div>
                    )}
                    {day.stay.type === "eigen boeking" && day.stay.url && (
                      <div className="flex gap-2 mt-2.5">
                        <a data-testid={`own-stay-link-${day.day_number}`} href={day.stay.url} target="_blank" rel="noreferrer"
                          className={`text-[11px] font-bold text-white rounded-full px-3 py-1.5 hover:opacity-85 transition-opacity duration-200 ${/booking\.com/i.test(day.stay.url) ? "bg-[#003580]" : /airbnb\./i.test(day.stay.url) ? "bg-[#FF385C]" : "bg-[#3A6EA5]"}`}>
                          {/booking\.com/i.test(day.stay.url) ? "Open op Booking.com" : /airbnb\./i.test(day.stay.url) ? "Open op Airbnb" : "Open je boeking"}
                        </a>
                      </div>
                    )}
                    {day.stay.type === "eigen boeking" && day.alt_stay && (
                      <div data-testid={`alt-stay-${day.day_number}`} className="mt-2.5 bg-secondary/60 rounded-lg p-2.5">
                        <p className="text-[10px] tracking-wider uppercase font-bold text-[#B5838D]">AI-alternatief</p>
                        <p className="text-xs font-semibold mt-0.5">
                          <a href={infoUrl(day.alt_stay.name, (day.end?.name || "").split(",")[0])} target="_blank" rel="noreferrer"
                            className="hover:text-[#3A6EA5] underline decoration-dotted underline-offset-2 transition-[color] duration-200">{day.alt_stay.name}</a>
                          <span className="font-normal text-muted-foreground"> ({day.alt_stay.type})</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{day.alt_stay.description}</p>
                        {day.alt_stay.price_indication && <p className="text-xs font-mono text-muted-foreground mt-0.5">{day.alt_stay.price_indication}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(day.activities || []).length > 0 && (
              <div className="mb-4">
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-accent mb-2">Activiteiten</p>
                <div className="space-y-2">
                  {day.activities.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Camera className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <p>
                        <a data-testid={`activity-link-${day.day_number}-${i}`} href={infoUrl(a.name, (day.end?.name || "").split(",")[0])} target="_blank" rel="noreferrer"
                          title="Meer info op internet"
                          className="font-semibold hover:text-accent underline decoration-dotted underline-offset-2 transition-[color] duration-200">{a.name}</a>
                        <span className="text-muted-foreground"> — {a.description}</span>
                        {a.suggested_by && <span className="text-[10px] font-bold text-[#3A6EA5] bg-[#3A6EA5]/10 rounded-full px-2 py-0.5 ml-1.5 whitespace-nowrap">tip van {a.suggested_by}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(day.food || []).length > 0 && (
              <div>
                <p className="text-xs tracking-[0.15em] uppercase font-semibold text-amber-600 mb-2">Eten & drinken</p>
                <div className="space-y-2">
                  {day.food.map((f, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <UtensilsCrossed className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                      <p>
                        <a data-testid={`food-link-${day.day_number}-${i}`} href={infoUrl(f.name, (day.end?.name || "").split(",")[0])} target="_blank" rel="noreferrer"
                          title="Meer info op internet"
                          className="font-semibold hover:text-amber-600 underline decoration-dotted underline-offset-2 transition-[color] duration-200">{f.name}</a>
                        <span className="text-muted-foreground"> — {f.description}</span>
                        {f.suggested_by && <span className="text-[10px] font-bold text-[#3A6EA5] bg-[#3A6EA5]/10 rounded-full px-2 py-0.5 ml-1.5 whitespace-nowrap">tip van {f.suggested_by}</span>}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!readOnly && day.type !== "stay" && (
              <div className="mt-4 pt-3 border-t border-border/50 flex items-center gap-5 flex-wrap">
                <AddStopoverDialog trip={trip} onRefresh={onRefresh} dayNumber={day.day_number} />
                <ViaPointsDialog trip={trip} onRefresh={onRefresh} />
                {(day.distance_km || 0) >= 60 && <SplitDayDialog trip={trip} day={day} onRefresh={onRefresh} />}
                {!day.stay && day.end?.lat != null && di < days.length - 1 && (
                  <ExtraNightsDialog trip={trip} day={day} onRefresh={onRefresh} testId={`extra-night-bottom-${day.day_number}`} />
                )}
              </div>
            )}
            </>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
};
